import { useMutationPostClassesStorePurchase } from "api/reactQuery/mutations/classesStorePurchase"
import { ITeacherStorePurchaseResult } from "api/reactQuery/mutations/classesStorePurchase.types"
import { useCustomSnackbar } from "hooks/snackbar"
import mixpanel from "mixpanel-browser"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "react-query"
import { getErrorMessageFromTab } from "utils/api"

import {
  IStoreChargeStudent,
  StoreChargeEntry,
} from "./SendStoreChargeDialog.types"
import {
  getCartAnalytics,
  ICartLine,
  parseStockConflict,
  patchStoreItemsConflict,
  patchStoreItemsStock,
  toPurchaseItems,
} from "./SendStoreChargeDialog.utils"

interface IUseStoreChargePurchaseArgs {
  classId: number
  entry: StoreChargeEntry
  cart: ICartLine[]
  students: IStoreChargeStudent[]
  isWholeClass: boolean
  excludedStudentIds: number[]
  studentCount: number
  onSuccess: (result: ITeacherStorePurchaseResult) => void
  // 409 — nobody charged. `isReadable` false means the short lines are unknown and stock is being refetched.
  onStockConflict: (isReadable: boolean) => void
}

// Submits the cart, keeps the items cache in step with the response and fires the analytics triad.
export const useStoreChargePurchase = ({
  classId,
  entry,
  cart,
  students,
  isWholeClass,
  excludedStudentIds,
  studentCount,
  onSuccess,
  onStockConflict,
}: IUseStoreChargePurchaseArgs) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { showSnackbar } = useCustomSnackbar()

  const { mutate, isLoading: isSubmitting } =
    useMutationPostClassesStorePurchase({
      options: {
        onSuccess: (response, variables) => {
          const res = response.data
          patchStoreItemsStock(queryClient, variables.classId, res.items ?? [])
          queryClient.invalidateQueries("classesStudentsCopy")

          const cartAnalytics = getCartAnalytics(cart)
          const outcome = {
            whole_class: isWholeClass,
            students_selected: studentCount,
            students_charged: res.charged_student_ids.length,
            students_skipped: res.skipped_student_ids.length,
            ...cartAnalytics,
          }
          window.dataLayer = window.dataLayer || []
          window.dataLayer.push({ event: "store_charge", ...outcome })
          if (window.Intercom) {
            window.Intercom("trackEvent", "store-charge-sent", {
              students_charged: res.charged_student_ids.length,
              line_count: cartAnalytics.line_count,
            })
          }
          mixpanel.track("send_store_charge", { entry, ...outcome })

          onSuccess(res)
        },
        onError: (error) => {
          if (error.response?.status === 409) {
            const lines = parseStockConflict(error)
            onStockConflict(!!lines)
            if (lines) {
              patchStoreItemsConflict(queryClient, classId, lines)
            } else {
              queryClient.invalidateQueries(["classesStoreItems", classId])
            }
            return
          }
          showSnackbar({
            title: t(getErrorMessageFromTab(error)),
            variant: "error",
          })
        },
      },
    })

  const charge = () => {
    const purchaseItems = toPurchaseItems(cart)
    mutate({
      classId,
      data: isWholeClass
        ? {
            items: purchaseItems,
            send_to_whole_class: true,
            excluded_student_ids: excludedStudentIds,
          }
        : { items: purchaseItems, student_ids: students.map((s) => s.id) },
    })
  }

  return { charge, isSubmitting }
}
